import { OverlayScrollbarsComponent as Scrollbars } from 'overlayscrollbars-react';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { filterUnique, filterWithout } from 'lib/filter';
import { SelectListItemWrapper, StyledSelectList } from './SelectList.styled';
import { getSelectionRangeIds } from './SelectList.utils';
import { SelectListItem } from './SelectListItem';
import { SelectListProps } from './types';

export const SelectList = <TValue extends string | number>(props: SelectListProps<TValue>) => {
  const { disabled = false, multiple = true, options = [], size, value = [], onChange } = props;

  // Multi-selection functionality state
  const [prevOptionIndex, setPrevOptionIndex] = useState<number>();
  const [prevOptionRange, setPrevOptionRange] = useState<Array<TValue>>();

  // Calculate the maximum possible list size when applicable
  const listSize = useMemo(
    () => (size ? Math.max(1, Math.min(options.length, size)) : undefined),
    [options.length, size],
  );

  /**
   * Handle clicking on an option item
   *
   * @param {MouseEvent} event Source mouse event
   * @param {number} index Index of the (de)selected option
   * @param {boolean} select TRUE if the option is now selected, FALSE otherwise
   */
  const handleOptionSelect = useCallback(
    (event: MouseEvent<HTMLElement>, index: number, selected: boolean) => {
      if (!onChange) {
        return;
      }

      // Get a list of entity ids within the calculated range
      const range = getSelectionRangeIds(options, index, index);

      // Handle single row selection
      if (!multiple) {
        if (selected) {
          return onChange(range);
        }

        // Clear selection when deselecting the current option
        return onChange([]);
      }

      // Shift key not being pressed
      if (!event.shiftKey) {
        if (!event.ctrlKey && !event.metaKey) {
          // Replace previous selection with the current range (single row) if not holding Ctrl key
          onChange(range);
        } else {
          if (selected) {
            // Add row to the current selection
            const values = filterUnique(value, range);
            onChange(values);
          } else {
            // Remove clicked row from the selection
            const values = filterWithout(value, range);
            onChange(values);
          }
        }

        // Store index of the current row for use with shift-select
        setPrevOptionIndex(index);

        // Unset range of selected rows when only selecting one row
        setPrevOptionRange(undefined);
      } else {
        // Determine index of the previously clicked row
        const lastRowIndex = prevOptionIndex ?? index;

        // Get a list of entity ids within the calculated range
        const range = getSelectionRangeIds(options, index, lastRowIndex);

        // Exclude previously selected range if the user was also holding Shift button
        const selection = prevOptionRange ? filterWithout(value, prevOptionRange) : value;
        const values = filterUnique(selection, range);
        onChange(values);

        // Store current range for when user selects another range right after this
        setPrevOptionRange(range);
      }
    },
    [multiple, options, prevOptionIndex, prevOptionRange, value, onChange],
  );

  // Build list of option items
  const children = useMemo(
    () => (
      <SelectListItemWrapper $size={listSize}>
        {options.map((option, index) => (
          <SelectListItem
            key={option.value}
            disabled={disabled}
            index={index}
            option={option}
            selected={value.includes(option.value)}
            onSelect={handleOptionSelect}
          />
        ))}
      </SelectListItemWrapper>
    ),
    [disabled, handleOptionSelect, listSize, options, value],
  );

  return (
    <StyledSelectList $disabled={disabled} tabIndex={0}>
      {size ? (
        <Scrollbars
          options={{
            overflowBehavior: {
              x: disabled ? 'hidden' : 'scroll',
              y: disabled ? 'hidden' : 'scroll',
            },
          }}
        >
          {children}
        </Scrollbars>
      ) : (
        children
      )}
    </StyledSelectList>
  );
};
